import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { isAdminSubdomain } from '@/utils/subdomainDetection';
import { SubdomainHandler } from '@/components/routing/SubdomainHandler';
import { AuthProvider } from '@/contexts/AuthContext';
import { AdminAuthProvider } from '@/contexts/AdminAuthContext';
import { AccessibilityProvider } from '@/components/ui/AccessibilityProvider';
import { PhoneCollectionProvider } from '@/components/onboarding/PhoneCollectionProvider';
import { EmailCollectionProvider } from '@/components/onboarding/EmailCollectionProvider';
import { LoadingProvider } from '@/contexts/LoadingContext';
import { GuestCurrencyProvider } from '@/contexts/GuestCurrencyContext';
import {
  ErrorBoundary,
  PaymentErrorFallback,
  QuoteFormErrorFallback,
  AdminErrorFallback,
} from '@/components/ui/ErrorBoundary';
import { QueryProvider } from './providers/QueryProvider';
import { SkeletonProvider } from './providers/SkeletonProvider';
import { Toaster } from '@/components/ui/toaster';
import { publicTestRoutes, protectedTestRoutes } from './routing/testRoutes';
import { publicRoutes } from './routing/publicRoutes';
import { authRoutes } from './routing/authRoutes';
import { customerRoutes } from './routing/customerRoutes';
import { adminRoutes } from './routing/adminRoutes';
import { paymentRoutes } from './routing/paymentRoutes';
import { coreRoutes } from './routing/coreRoutes';


import Layout from '@/components/layout/Layout';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import AdminProtectedRoute from '@/components/auth/AdminProtectedRoute';
// Back to original component now that we know the data works
// QuotesListPage import moved to ./routing/adminRoutes.tsx

// Core page imports moved to ./routing/coreRoutes.tsx
const NotFound = React.lazy(() => import('@/pages/NotFound'));

// Temporary demo import for flag comparison
const FlagComparisonDemo = React.lazy(() => import('@/demo/FlagComparisonDemo'));

// Error Recovery System Demo
const ErrorRecoveryDemo = React.lazy(() => import('@/pages/ErrorRecoveryDemo'));



import { StatusConfigProvider } from './providers/StatusConfigProvider';

const router = createBrowserRouter([
  {
    path: 'admin',
    element: <AdminProtectedRoute />,
    children: adminRoutes,
  },
  // Development routes (public access)
  // Demo routes (public access for design review) - temporarily enabled for flag demo
  {
    path: 'demo',
    children: [
      {
        path: 'flag-comparison',
        element: <FlagComparisonDemo />,
      },
      {
        path: 'error-recovery',
        element: <ErrorRecoveryDemo />,
      },
    ],
  },
  ...authRoutes,
  {
    path: '/',
    element: (
      <SubdomainHandler>
        <Layout />
      </SubdomainHandler>
    ),
    children: [
      ...coreRoutes,
      ...publicRoutes,
      ...publicTestRoutes,
      ...paymentRoutes,
      {
        element: <ProtectedRoute />,
        children: [
          ...protectedTestRoutes,
          ...customerRoutes,
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

function App() {
  // CRITICAL DEBUG: Track if App component itself is re-rendering constantly
  const appRenderTimestamp = Date.now();
  
  return (
    <ErrorBoundary>
      <QueryProvider>
        <SkeletonProvider>
          <AuthProvider>
            <AdminAuthProvider>
              <GuestCurrencyProvider>
              <LoadingProvider>
                <AccessibilityProvider>
                <StatusConfigProvider>
                  <HelmetProvider>
                    <Suspense fallback={null}>
                      <RouterProvider router={router} />
                    </Suspense>
                    <Toaster />
                  </HelmetProvider>
                  <PhoneCollectionProvider />
                  <EmailCollectionProvider />
                </StatusConfigProvider>
              </AccessibilityProvider>
              </LoadingProvider>
            </GuestCurrencyProvider>
            </AdminAuthProvider>
          </AuthProvider>
        </SkeletonProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}

export default App;
